import React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Bone } from '@lightspeed/flame/Bone';
import { Button } from '@lightspeed/flame/Button';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Input } from '@lightspeed/flame/Input';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Text } from '@lightspeed/flame/Text';

import { datadogRum } from 'root/datadog/datadogRum';

import {
  DueAmountFormats,
  usePaymentTermsInputContext,
} from '../contexts/CreditStatementTermsContext';
import {
  emailCreditStatement,
  fetchPrimaryCustomerEmail,
  getPaymentLink,
  hasCrossedPaymentLinkMaxMinAmount,
  updateAccountEmailRequest,
} from '../utils';

export const ERROR_MESSAGES = {
  UPDATE_FAILED: 'Update failed',
  EMAILING_FAILED: 'Emailing failed',
};

const EmailModal: React.FC<{
  hasLightSpeedPayments: boolean;
}> = ({ hasLightSpeedPayments }) => {
  const {
    globalState: { printDocumentLanguage: language, startDate, endDate, includePaymentLink },
    constantProps: {
      employeeId,
      registerId,
      shopId,
      creditAccount: { id: creditAccountId, name, customerId },
      printDocumentLanguageCount,
    },
    setGlobalState,
    getDueAmount,
    getDueDate,
  } = usePaymentTermsInputContext();

  const [modalOpen, setModalOpen] = React.useState(true);
  const [inputValue, setInputValue] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const [isChecked, setIsChecked] = React.useState(false);

  const [isError, setIsError] = React.useState(false);
  const [errorWithCustomerProfileUpdate, setErrorWithCustomerProfileUpdate] = React.useState(false);
  const [_, setErrorEmailingStatement] = React.useState(false);

  const { id: accountId } = window.merchantos.account;

  React.useEffect(() => {
    const fetchCustomerEmail = async () => {
      setIsLoading(true);

      const primaryEmail = await fetchPrimaryCustomerEmail(customerId);

      setInputValue(primaryEmail);
      setIsLoading(false);
    };

    fetchCustomerEmail();
  }, [accountId, customerId]);

  const setEmailingFailedError = () => {
    setIsError(true);
    setErrorEmailingStatement(true);
    setErrorWithCustomerProfileUpdate(false);
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setIsError(false);
    setErrorEmailingStatement(false);
    setErrorWithCustomerProfileUpdate(false);

    const dueAmountCents = getDueAmount(DueAmountFormats.CENTS);
    const dueDate = getDueDate();

    const { overMaxAmount, belowMinAmount } = hasCrossedPaymentLinkMaxMinAmount(dueAmountCents);

    let shouldGeneratePaymentLink = false;

    if (
      hasLightSpeedPayments &&
      includePaymentLink &&
      dueAmountCents !== 0 &&
      !belowMinAmount &&
      !overMaxAmount
    ) {
      shouldGeneratePaymentLink = true;
    }

    let paymentLink: string | undefined;
    // when "Send" is clicked, request payment link first if the merchant has LS payments
    // and the amount is not more than PAY_LINK_AMOUNT_LIMIT
    if (shouldGeneratePaymentLink) {
      if (registerId === '' || registerId === '0') {
        window.showNotifier(
          'warning',
          'Unable to email statement. Select a register and try again.'
        );
        setIsLoading(false);
        handleModalClose();
        return false;
      }

      paymentLink = await getPaymentLink({
        amount: dueAmountCents.toString(),
        language,
        creditAccountId,
        shopId,
        registerId,
        employeeId,
      });

      // if fails, keep user on modal, show alert, user can click "Send" to try again
      if (!paymentLink) {
        const err = new Error('Fetching payment link failed');
        datadogRum.addError(err, { accountId, customerId });
        setEmailingFailedError();
        return false;
      }
    }

    if (isChecked) {
      // if the update to customer profile checkbox is checked, send the request to update the customer profile first
      // if successful, send request to send email
      // if fails, we do not send the request to send email, user stays on modal and can try again
      const checkIsCustomerProfileUpdated = await handleUpdateEmailToCustomerProfile();
      if (!checkIsCustomerProfileUpdated) return false;
    }

    // if the update to customer profile checkbox is not checked, or all above requests succeed, send request to send email
    const res = await emailCreditStatement(
      customerId,
      inputValue,
      startDate,
      endDate,
      language,
      paymentLink,
      dueDate,
      getDueAmount(DueAmountFormats.DECIMAL).toString()
    );

    if (!res) {
      // if fails, keep user on modal, show the "Emailing failed" alert, user can click send to try again
      setEmailingFailedError();
      return false;
    }

    // if successful, close the modal and show the success notifier
    setIsLoading(false);
    handleModalClose();
    window.flashNotifier(
      'success',
      `Account statement emailed to <var notranslate>${inputValue}</var>`,
      10
    );
    return true;
  };

  const handleUpdateEmailToCustomerProfile = async () => {
    if (await updateAccountEmailRequest(accountId, customerId, inputValue)) {
      setErrorWithCustomerProfileUpdate(false);
      return true;
    }
    setIsError(true);
    setErrorWithCustomerProfileUpdate(true);
    return false;
  };

  const handleUpdateCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(event.target.checked);
  };

  const handleModalClose = () => {
    if (parseInt(printDocumentLanguageCount, 10) > 1) {
      setGlobalState((prevState) => ({
        ...prevState,
        openModal: 'none',
        printDocumentLanguage: '',
      }));
    } else {
      setGlobalState((prevState) => ({
        ...prevState,
        openModal: 'none',
      }));
    }

    setModalOpen(false);
  };

  return (
    <Modal isOpen={modalOpen} onRequestClose={handleModalClose} css={{ width: '435px' }}>
      <ModalHeader
        data-test="modal-header-customer-name"
        style={{ paddingTop: '8px', paddingBottom: '8px' }}
      >
        Send Account Statement{' '}
        {name && (
          <span>
            to{' '}
            <span className="notranslate" data-test="customer-name">
              {name}
            </span>
          </span>
        )}
      </ModalHeader>
      <ModalBody style={{ padding: '18px' }}>
        <Flex flexDirection="column">
          {isError && (
            <Alert type="danger" noCloseBtn data-test="error-alert" mb={2}>
              <Text fontSize="1rem" fontWeight="500">
                {errorWithCustomerProfileUpdate
                  ? ERROR_MESSAGES.UPDATE_FAILED
                  : ERROR_MESSAGES.EMAILING_FAILED}
              </Text>
            </Alert>
          )}
          <Box>
            {isLoading ? (
              <Flex flexDirection="column" data-test="loading-wrapper">
                <Box width="100%">
                  <Bone height={4} />
                </Box>
                <Box width="100%" mt={2}>
                  <Bone height={4} />
                </Box>
              </Flex>
            ) : (
              <>
                <Input
                  type="email"
                  placeholder="Enter a recipient email"
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  style={{ paddingLeft: '12px', paddingRight: '12px', borderRadius: '3px' }}
                  autoFocus
                  autoComplete="off"
                  data-test="email-input"
                />
                <Flex mt={2}>
                  <input
                    type="checkbox"
                    onChange={(event) => handleUpdateCheckbox(event)}
                    checked={isChecked}
                    title="Update email to customer profile"
                    id="updateCustomerProfileCheckbox"
                    data-test="update-to-customer-profile-checkbox"
                  />
                  <Box ml={2} fontSize="14px" lineHeight="18px">
                    <label htmlFor="updateCustomerProfileCheckbox">
                      Update email to customer profile
                    </label>
                  </Box>
                </Flex>
              </>
            )}
          </Box>
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Flex justifyContent="flex-end">
          <Button mr={2} onClick={handleModalClose} data-test="cancel-button" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            disabled={isLoading || !inputValue}
            variant="secondary"
            fill
            data-test="send-button"
            onClick={handleSubmit}
          >
            Send
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

export default EmailModal;
